import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ArrowRight,
  Award,
  BadgeCheck,
  Briefcase,
  Building2,
  CheckCircle2,
  CircleDot,
  Coffee,
  Handshake,
  MapPin,
  ShieldCheck,
  Sparkles,
  Store,
  TrendingUp,
  Users,
} from "lucide-react";
import { Reveal, SectionTag } from "@/components/Reveal";
import EditorialMarquee from "@/components/EditorialMarquee";
import api from "@/api";
import Seo, { breadcrumbSchema, webPageSchema } from "@/components/Seo";

const inputCls = "w-full rounded-xl border border-clayline bg-cream px-4 py-3 text-charcoal placeholder:text-charcoal/40 focus:outline-none focus:ring-2 focus:ring-terracotta/60";
const MODEL_ICONS = [BadgeCheck, Store, Briefcase];
const FEATURE_ICONS = [ShieldCheck, TrendingUp, CheckCircle2, Users];
const WHY_ICONS = [TrendingUp, Award, Handshake, Store, Sparkles];
const STORE_ICONS = [Store, Coffee, Building2];
const storeImageSrc = (path) => (
  path && (path.startsWith("http") || path.startsWith("/"))
    ? path
    : `${process.env.REACT_APP_BACKEND_URL}/api/files/${path}`
);
const storeTestId = (index) => ["kiosk", "cafe", "flagship"][index] || `store-${index + 1}`;

const Franchise = () => {
  const [content, setContent] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", budget: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/franchise").then((r) => setContent(r.data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const selectModel = (model) => {
    setForm((f) => ({
      ...f,
      message: `I'm interested in the ${model.code} model (${model.name}). Please share the next steps.`,
    }));
    document.getElementById("franchise-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/enquiries", { ...form, type: "franchise" });
      toast.success("Enquiry received. Our franchise team will call you within 48 hours.");
      setForm({ name: "", email: "", phone: "", city: "", budget: "", message: "" });
    } catch {
      toast.error("Could not send your enquiry. Please try again.");
    } finally {
      setSending(false);
    }
  };

  if (!content) {
    return <div className="min-h-screen flex items-center justify-center font-mono text-sm text-charcoal/50" data-testid="franchise-loading">Brewing the numbers…</div>;
  }

  const models = content.models || [];
  const detail = (model, labels) => {
    const wanted = (Array.isArray(labels) ? labels : [labels]).map((label) => label.toLowerCase());
    return model?.details?.find((d) => wanted.includes(d.label.toLowerCase()));
  };
  const detailText = (model, labels) => {
    const item = detail(model, labels);
    return item ? `${item.value}${item.sub ? ` ${item.sub}` : ""}` : "—";
  };
  const earningText = (model) => {
    if (model?.code === "FICO") return detailText(model, "Annual Returns");
    if (model?.code === "FOCO") return detailText(model, "Profit Sharing");
    return detailText(model, "Royalty");
  };
  const operationsText = (model) => (model?.code === "FOFO" ? "Your team, with Chai Times support" : "Chai Times");
  const involvementText = (model) => {
    if (model?.code === "FICO") return "Fully managed / passive";
    if (model?.code === "FOCO") return "Own the asset, skip daily operations";
    return "Hands-on ownership";
  };

  return (
    <div data-testid="franchise-page">
      <Seo
        title="Chai Franchise Opportunity | Chai Times"
        description="Partner with Chai Times to open a kulhad chai café franchise. Explore FICO, FOCO and FOFO investment models, returns, royalties and brand support."
        path="/franchise"
        image="/franchise-poster.jpg"
        schema={[
          webPageSchema({
            name: "Chai Times Franchise Opportunity",
            description: "Explore Chai Times FICO, FOCO and FOFO café franchise models, investment support and territory opportunities.",
            path: "/franchise",
          }),
          breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Franchise", path: "/franchise" }]),
        ]}
      />

      {/* Hero */}
      <section className="pt-32 md:pt-40 pb-16 md:pb-24 relative overflow-hidden" data-testid="franchise-hero">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <Reveal>
            <span className="inline-flex items-center gap-2 rounded-full border border-clayline bg-sand/70 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.25em] text-forest" data-testid="franchise-eyebrow">
              <CircleDot size={10} className="text-terracotta" /> {content.eyebrow}
            </span>
          </Reveal>
          <div className="grid lg:grid-cols-12 gap-12 mt-8 items-center">
            <Reveal delay={0.05} className="lg:col-span-7">
              <h1 className="font-serif font-black text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[1.0]" data-testid="franchise-heading">
                {content.title_dark}<br />
                <span className="italic text-terracotta">{content.title_accent}</span>
              </h1>
              <p className="mt-6 font-mono text-xs uppercase tracking-[0.22em] text-forest" data-testid="franchise-subtitle">{content.subtitle}</p>
              <p className="mt-5 max-w-2xl text-charcoal/70 leading-relaxed text-base md:text-lg" data-testid="franchise-intro">
                {content.brand_intro}
              </p>
              <div className="mt-8 flex flex-wrap gap-3" data-testid="franchise-brand-features">
                {(content.brand_features || []).map((feature, i) => (
                  <span key={feature} className="rounded-full border border-clayline bg-cream px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] text-charcoal/70" data-testid={`franchise-brand-feature-${i}`}>
                    {feature}
                  </span>
                ))}
              </div>
              <div className="mt-9 flex flex-wrap gap-4">
                <a href="#franchise-models" data-testid="franchise-explore-models" className="rounded-full px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] bg-terracotta text-cream hover:bg-forest transition-colors inline-flex items-center gap-2">
                  Explore 3 Models <ArrowRight size={14} />
                </a>
                <a href="#franchise-form" data-testid="franchise-hero-enquire" className="rounded-full px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] border border-clayline hover:border-terracotta hover:text-terracotta transition-colors">
                  Send Enquiry
                </a>
              </div>
            </Reveal>
            <Reveal delay={0.12} className="lg:col-span-5">
              <div className="relative rounded-3xl overflow-hidden border border-clayline shadow-2xl aspect-[4/5] bg-forest" data-testid="franchise-hero-media">
                <img src="/adda-pic.webp" alt="Chai Times modern chai café interior" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-forestdeep/90 via-forest/20 to-transparent" />
                <div className="absolute bottom-0 p-7 text-cream">
                  <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold">Three proven paths</p>
                  <p className="font-serif font-black text-3xl mt-2">FICO · FOCO · FOFO</p>
                </div>
              </div>
            </Reveal>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
            {(content.hero_stats || []).map((s, i) => (
              <Reveal key={i} delay={0.1 + i * 0.07}>
                <div className="border border-clayline rounded-2xl bg-cream p-6 hover:shadow-lg hover:border-terracotta/50 transition-all" data-testid={`franchise-hero-stat-${i}`}>
                  <p className={`font-mono font-bold text-3xl md:text-4xl ${s.accent ? "text-terracotta" : "text-forest"}`}>{s.value}</p>
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-charcoal mt-2">{s.label}</p>
                  <p className="text-sm text-charcoal/55 mt-1">{s.sub}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <EditorialMarquee text="FICO • FOCO • FOFO • Brew a business with Chai Times" />

      {/* Models */}
      <section id="franchise-models" className="py-20 md:py-28 bg-sand/50 border-y border-clayline/60 scroll-mt-20" data-testid="franchise-models-section">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <Reveal className="max-w-3xl">
            <SectionTag>Three Franchise Models</SectionTag>
            <h2 className="font-serif font-black text-4xl md:text-6xl tracking-tight mt-4">
              Choose how involved <span className="italic text-terracotta">you want to be.</span>
            </h2>
            <p className="mt-5 text-charcoal/65 leading-relaxed max-w-2xl">
              From a fully managed investment to hands-on ownership, each model keeps the Chai Times brew, brand and operating standards at the centre.
            </p>
          </Reveal>
          <div className="grid lg:grid-cols-3 gap-7 mt-14">
            {models.map((model, i) => {
              const Icon = MODEL_ICONS[i % MODEL_ICONS.length];
              return (
                <Reveal key={model.code} delay={i * 0.09}>
                  <article className="group h-full rounded-3xl overflow-hidden border border-clayline bg-cream shadow-sm hover:shadow-2xl hover:-translate-y-1 hover:border-terracotta/50 transition-all duration-300 flex flex-col" data-testid={`franchise-model-${model.code.toLowerCase()}`}>
                    <div className="bg-forest text-cream p-7 relative overflow-hidden">
                      <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full border border-gold/20" />
                      <span className="inline-flex rounded-full bg-terracotta px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em]" data-testid={`franchise-model-badge-${model.code.toLowerCase()}`}>
                        Model {i + 1}
                      </span>
                      <div className="mt-6 flex items-start justify-between gap-4">
                        <div>
                          <h3 className="font-serif font-black text-4xl" data-testid={`franchise-model-code-${model.code.toLowerCase()}`}>{model.code}</h3>
                          <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-cream/70 mt-2">{model.name}</p>
                        </div>
                        <span className="w-12 h-12 rounded-2xl bg-cream/10 border border-cream/15 flex items-center justify-center text-gold shrink-0"><Icon size={22} /></span>
                      </div>
                      <p className="font-serif italic text-2xl text-gold mt-7 leading-tight">{model.tagline}</p>
                    </div>
                    <div className="p-7 flex-1 flex flex-col">
                      <p className="text-charcoal/70 leading-relaxed text-sm md:text-base">{model.intro}</p>
                      <div className="grid sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-3 mt-7">
                        {(model.features || []).map((feature, fi) => {
                          const FeatureIcon = FEATURE_ICONS[fi % FEATURE_ICONS.length];
                          return (
                            <div key={feature} className="flex gap-3 items-start rounded-xl bg-sand/60 border border-clayline/60 p-3" data-testid={`franchise-model-feature-${model.code.toLowerCase()}-${fi}`}>
                              <FeatureIcon size={17} className="text-terracotta shrink-0 mt-0.5" />
                              <span className="text-sm text-charcoal/75 leading-snug">{feature}</span>
                            </div>
                          );
                        })}
                      </div>
                      <div className="mt-7 pt-6 border-t border-clayline/70">
                        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-terracotta mb-4">Key Details</p>
                        <div className="grid grid-cols-2 gap-3">
                          {(model.details || []).map((item, di) => (
                            <div key={`${item.label}-${di}`} className="rounded-xl border border-clayline bg-cream p-4" data-testid={`franchise-model-detail-${model.code.toLowerCase()}-${di}`}>
                              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-charcoal/50">{item.label}</p>
                              <p className="font-mono font-bold text-2xl text-forest mt-2">{item.value}</p>
                              <p className="text-xs text-charcoal/55 mt-1">{item.sub}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="mt-6 rounded-2xl bg-forest/10 border border-forest/15 p-4 flex gap-3" data-testid={`franchise-model-note-${model.code.toLowerCase()}`}>
                        <CheckCircle2 size={18} className="text-forest shrink-0 mt-0.5" />
                        <p className="text-sm text-charcoal/75 leading-relaxed">{model.note}</p>
                      </div>
                      <button type="button" onClick={() => selectModel(model)} data-testid={`franchise-model-cta-${model.code.toLowerCase()}`} className="mt-7 w-full rounded-full px-6 py-4 font-mono text-xs uppercase tracking-[0.18em] bg-terracotta text-cream hover:bg-forest transition-colors inline-flex items-center justify-center gap-2">
                        {model.cta} <ArrowRight size={14} />
                      </button>
                    </div>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Store types */}
      <section id="store-types" className="py-20 md:py-28 bg-cream border-b border-clayline/60 scroll-mt-20" data-testid="franchise-store-types-section">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <Reveal className="max-w-3xl">
            <SectionTag>Store Types</SectionTag>
            <h2 className="font-serif font-black text-4xl md:text-6xl tracking-tight mt-4" data-testid="franchise-store-types-heading">
              Choose Your <span className="italic text-terracotta">Store Type</span>
            </h2>
            <p className="mt-5 text-charcoal/65 leading-relaxed max-w-2xl">{content.store_sub}</p>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-7 mt-14">
            {(content.store_types || []).map((store, i) => {
              const Icon = STORE_ICONS[i % STORE_ICONS.length];
              const testId = storeTestId(i);
              return (
                <Reveal key={store.name} delay={i * 0.08}>
                  <article className="group h-full rounded-3xl overflow-hidden border border-clayline bg-sand/35 shadow-sm hover:shadow-xl hover:-translate-y-1 hover:border-terracotta/50 transition-all duration-300 flex flex-col" data-testid={`store-type-card-${testId}`}>
                    <div className="relative aspect-[16/10] overflow-hidden bg-forest">
                      {store.image && (
                        <img src={storeImageSrc(store.image)} alt={`${store.name} Chai Times store format`} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-testid={`store-type-image-${testId}`} />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-forest/85 via-forest/10 to-transparent" />
                      <span className="absolute bottom-4 left-4 inline-flex items-center gap-1.5 rounded-full border border-gold/30 bg-forest/90 backdrop-blur-md px-3.5 py-1.5 font-mono text-[11px] font-bold text-gold tracking-wider" data-testid={`store-type-area-${testId}`}>
                        <MapPin size={13} /><span>Area: {store.area}</span>
                      </span>
                    </div>
                    <div className="p-7 flex-1 flex flex-col">
                      <div className="flex items-center gap-4">
                        <span className="w-11 h-11 rounded-xl bg-forest/10 text-forest flex items-center justify-center shrink-0"><Icon size={20} /></span>
                        <h3 className="font-sans font-bold text-xl uppercase tracking-wide text-forest" data-testid={`store-type-name-${testId}`}>{store.name}</h3>
                      </div>
                      <p className="mt-4 text-charcoal/70 text-sm md:text-base leading-relaxed">{store.desc}</p>
                      <div className="mt-6 pt-5 border-t border-clayline/70">
                        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-terracotta font-semibold mb-3">Key Features</p>
                        <ul className="space-y-3">
                          {(store.features || []).map((feature, fi) => (
                            <li key={feature} className="flex items-start gap-3 text-sm text-charcoal/80 leading-snug" data-testid={`store-type-feature-${testId}-${fi}`}>
                              <span className="w-1.5 h-1.5 rounded-full bg-terracotta mt-2 shrink-0" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="py-20 md:py-28" data-testid="franchise-comparison-section">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <Reveal className="text-center max-w-2xl mx-auto">
            <SectionTag>Quick Compare</SectionTag>
            <h2 className="font-serif font-black text-4xl md:text-5xl tracking-tight mt-4">Three models. One brand standard.</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-12 overflow-x-auto rounded-3xl border border-clayline bg-cream shadow-xl" data-testid="franchise-comparison-table">
              <table className="w-full min-w-[760px] text-left">
                <thead className="bg-forest text-cream">
                  <tr>
                    <th className="px-6 py-5 font-mono text-[11px] uppercase tracking-[0.2em]">Compare</th>
                    {models.map((model) => <th key={model.code} className="px-6 py-5 font-serif text-2xl">{model.code}</th>)}
                  </tr>
                </thead>
                <tbody className="divide-y divide-clayline/60">
                  {[
                    ["Setup Investment", ...models.map((model) => detailText(model, ["Investment", "Setup Investment"]))],
                    ["Operated By", ...models.map(operationsText)],
                    ["Earning Structure", ...models.map(earningText)],
                    ["Your Involvement", ...models.map(involvementText)],
                  ].map((row, ri) => (
                    <tr key={row[0]} data-testid={`franchise-comparison-row-${ri}`}>
                      {row.map((cell, ci) => (
                        <td key={ci} className={`px-6 py-5 ${ci === 0 ? "font-mono text-[11px] uppercase tracking-[0.18em] text-charcoal/55" : "text-sm text-charcoal/75 font-semibold"}`}>{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Why partner */}
      <section className="bg-forest text-cream py-20 md:py-28 grain relative overflow-hidden" data-testid="franchise-why-partner">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <Reveal className="max-w-3xl">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold mb-3">Why partner with Chai Times?</p>
            <h2 className="font-serif font-black text-4xl md:text-6xl tracking-tight">A brand built for the long pour.</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mt-14">
            {(content.why_partner || []).map((item, i) => {
              const Icon = WHY_ICONS[i % WHY_ICONS.length];
              return (
                <Reveal key={item.title} delay={i * 0.07}>
                  <div className="h-full rounded-2xl border border-cream/15 bg-forestdeep/70 p-6 hover:border-gold/50 hover:-translate-y-1 transition-all" data-testid={`franchise-why-partner-${i}`}>
                    <span className="w-11 h-11 rounded-xl bg-gold/15 text-gold flex items-center justify-center mb-5"><Icon size={20} /></span>
                    <h3 className="font-serif font-bold text-xl">{item.title}</h3>
                    <p className="text-sm text-cream/65 leading-relaxed mt-3">{item.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Enquiry form */}
      <section id="franchise-form" className="bg-forestdeep text-cream py-20 md:py-28 grain relative scroll-mt-20">
        <div className="max-w-3xl mx-auto px-5 md:px-10">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold mb-3">Claim your territory</p>
            <h2 className="font-serif font-black text-4xl md:text-5xl tracking-tight mb-10">Franchise enquiry</h2>
            <form onSubmit={submit} className="bg-forest border border-cream/15 rounded-2xl p-7 md:p-9" data-testid="franchise-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <input required placeholder="Full name" value={form.name} onChange={set("name")} className={`${inputCls} !bg-forest !border-cream/20 !text-cream placeholder:!text-cream/40`} data-testid="franchise-name-input" />
                <input required placeholder="Phone" value={form.phone} onChange={set("phone")} className={`${inputCls} !bg-forest !border-cream/20 !text-cream placeholder:!text-cream/40`} data-testid="franchise-phone-input" />
                <input type="email" placeholder="Email" value={form.email} onChange={set("email")} className={`${inputCls} !bg-forest !border-cream/20 !text-cream placeholder:!text-cream/40`} data-testid="franchise-email-input" />
                <input required placeholder="City / locality" value={form.city} onChange={set("city")} className={`${inputCls} !bg-forest !border-cream/20 !text-cream placeholder:!text-cream/40`} data-testid="franchise-city-input" />
              </div>
              <select value={form.budget} onChange={set("budget")} required className={`${inputCls} !bg-forest !border-cream/20 !text-cream mt-4`} data-testid="franchise-budget-select">
                <option value="">Investment capacity</option>
                <option value="15-20">₹15–20 Lakh</option>
                <option value="18-25">₹18–25 Lakh</option>
                <option value="25+">₹25 Lakh+</option>
              </select>
              <textarea rows={4} placeholder="Tell us which model interests you, your space, experience, or questions…" value={form.message} onChange={set("message")} className={`${inputCls} !bg-forest !border-cream/20 !text-cream placeholder:!text-cream/40 mt-4`} data-testid="franchise-message-input" />
              <button type="submit" disabled={sending} data-testid="franchise-submit-btn" className="mt-6 w-full rounded-full px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] bg-terracotta text-cream hover:bg-gold hover:text-charcoal transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60">
                {sending ? "Sending…" : "Send Enquiry"} <ArrowRight size={14} />
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Franchise;
